import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '@app/auth/services/auth.service';
import { CallCenterService } from '@app/call-center/services/call-center.service';
import { CallHistoryService } from '@app/call-history/services/call-history.service';
import { CallQualitySummaryService } from '@app/call-history/services/call-quality-summary.service';
import { ActiveChimeMeetingService } from '@app/chat/services/active-chime-meeting.service';
import { ChannelService } from '@app/chat/services/channel.service';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField } from '@app/core/models/branding.models';
import { EventName } from '@app/core/models/event-bus.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { BrandingService } from '@app/core/services/branding.service';
import { ConfigSettingUpdatedService } from '@app/core/services/config-setting-updated.service';
import { EventBusService } from '@app/core/services/event-bus.service';
import { FaviconService } from '@app/core/services/favicon.service';
import { HidRendererService } from '@app/core/services/hid-renderer.service';
import { KeyboardShortcutService } from '@app/core/services/keyboard-shortcut.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { deleteOrphanedDatabases } from '@app/core/utils/indexeddb-cleaner';
import { FaxService } from '@app/fax/services/fax.service';
import { MeetingsService } from '@app/meetings/services/meetings.service';
import { CallParkService } from '@app/parking/services/call-park.service';
import { LoggerStorageService } from '@app/phone/services/logger-storage.service';
import { PhoneService } from '@app/phone/services/phone.service';
import { RecordingService } from '@app/phone/services/recording.service';
import { RendererIntegrationsService } from '@app/preferences/services/renderer-integrations.service';
import { SMSService } from '@app/sms/services/sms.service';
import { SMSUnreadMessageService } from '@app/sms/services/sms-unread-message.service';
import { UnreadMessageService } from '@app/sms/services/unread-message.service';
import { VoicemailService } from '@app/voicemail/services/voicemail.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  defer,
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  pairwise,
  retry,
  startWith,
  Subscription,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { RENDERER_AUTH_TOKEN } from 'src/providers/renderer-auth-token-provider';

import { CustomRingtonesService } from './custom-ringtones.service';
import { FeatureAnnouncementService } from './feature-announcement.service';
import { UcLinkSocketService } from './uc-link-socket.service';

/** app_code returned by /provision when the account isn't provisioned for ConnectUC. */
const NOT_PROVISIONED_APP_CODE = 1002;
/** Transient (non-1002) provision failures are retried this many times before routing to /not-provisioned. */
const PROVISION_RETRY_COUNT = 2;
const PROVISION_RETRY_BACKOFF_MS = 500;

/*
 *
 * Some socket events are delivered as soon as the socket is connected.
 * For those events, it is important the interested service
 * is initialized _before_ the socket service, so we don't miss any events needed to bootstrap our application.
 * Angular provides
 * no way to explicitly control how services get initialized.
 *
 * Any service meeting the criteria outlined above should implement this interface
 * and be added to `websocketDependentServices`
 * an array in this class.
 */
@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  public isLoading$ = new BehaviorSubject(false);
  public initialLoading$ = new BehaviorSubject(false);
  public progress$ = new BehaviorSubject(0);

  private readonly stabilizedSubject = new BehaviorSubject<boolean>(false);
  public readonly stabilized$ = this.stabilizedSubject.asObservable();

  private readonly services = [
    this.contactService,
    this.voicemailService,
    this.callHistoryService,
    this.callParkService,
    this.channelService,
    this.smsService,
    this.faxService,
    this.integrationsService,
    this.hidRendererService,
    this.meetingsService,
  ];

  private websocketServices = [
    this.activeMeetingService,
    this.unreadMessageService,
    this.unreadSMSMessageService,
    this.ucLinkSocketService,
    this.voicemailService,
    this.callCenterService,
    this.configSettingUpdatedService,
  ];

  private dataFetchSubscription: Subscription | null = null;
  private bootstrapStartTime = 0;

  constructor(
    private voicemailService: VoicemailService,
    private callHistoryService: CallHistoryService,
    private contactService: ContactService,
    private channelService: ChannelService,
    private authService: AuthService,
    private callParkService: CallParkService,
    private smsService: SMSService,
    private activeMeetingService: ActiveChimeMeetingService,
    private unreadMessageService: UnreadMessageService,
    private unreadSMSMessageService: SMSUnreadMessageService,
    private faxService: FaxService,
    private webSocketService: WebSocketService,
    private appConfigService: AppConfigService,
    private featureAnnouncementService: FeatureAnnouncementService,
    private brandingService: BrandingService,
    // Added the recordingService and hidRendererService because it was needed to be initialized at the start of the app
    private recordingService: RecordingService,
    private hidRendererService: HidRendererService,
    private faviconService: FaviconService,
    private integrationsService: RendererIntegrationsService,
    private ucLinkSocketService: UcLinkSocketService,
    private eventBusService: EventBusService,
    private dialog: MatDialog,
    private phoneService: PhoneService,
    private router: Router,
    private meetingsService: MeetingsService,
    private injector: Injector,
    private loggerStorageService: LoggerStorageService,
    private callCenterService: CallCenterService,
    private configSettingUpdatedService: ConfigSettingUpdatedService,
    private customRingtonesService: CustomRingtonesService, // to get/update custom ringtones on start
    // Added the keyboardShortcutService because it was needed to be initialized at the start of the app
    private keyboardShortcutService: KeyboardShortcutService,
    // Added so its subscription to CallQualityService.summaryReady$ exists before any call ends --
    // otherwise a call made/ended before the user ever opens Call History would emit into a Subject
    // with no listener yet, and the summary would be lost (Subject has no replay). This also covers
    // CallQualityService itself needing to be instantiated at app start (it subscribes to
    // SipjsService.callMedia$): Angular DI constructs it as a dependency of this service too.
    private callQualitySummaryService: CallQualitySummaryService
  ) {}

  static init(injector: Injector): () => Promise<void> {
    const initializer = injector.get(AppInitializerService);
    return () =>
      new Promise((resolve) => {
        initializer.init();
        resolve();
      });
  }

  init() {
    this.bootstrapStartTime = performance.now();
    console.log('[APP-INITIALIZER] Starting app initialization');

    // Wire stabilization signal into LoggerStorageService so it knows when to flush to IDB
    this.stabilized$.subscribe((stabilized) => this.loggerStorageService.setStabilized(stabilized));

    // Fire-and-forget: delete orphaned databases from the pre-unified-DB era
    deleteOrphanedDatabases();

    // Set up logout event listener early, before any HTTP requests are made
    this.setupAuthEventHandling();

    // Note: Masquerade token handling is done in APP_INITIALIZER (app.module.ts) BEFORE this init() is called.
    // This ensures the masquerade token is set before any guards or services can load the old token.

    const authTokenAdapter = this.injector.get(RENDERER_AUTH_TOKEN);
    authTokenAdapter
      .observeTokenState()
      .pipe(
        // Drop the transient 'refreshing' state so pairwise compares SETTLED states only.
        // 'refreshing' is not a destination — it says a request is in flight, not what the token
        // ended up as, so it must not be the basis for a bootstrap decision. Keeping it made the
        // two paths that pass through it indistinguishable one step back:
        //   launch with an expired token: uninitialized → refreshing → valid  (must bootstrap)
        //   mid-session token refresh:    valid         → refreshing → valid  (must NOT bootstrap)
        // Both arrive at 'valid' with 'refreshing' behind them, and the old filter rejected that
        // pair, so an expired token at launch skipped the bootstrap entirely — no sockets, no
        // provision, no feature data (CUC-3845). Dropping it collapses the launch path to
        // uninitialized → valid and the refresh path to valid → valid, which the filter below
        // separates correctly. AuthService drops 'refreshing' the same way for the same reason.
        filter((tokenState) => tokenState?.state !== 'refreshing'),
        startWith(undefined), // Provide initial value for pairwise
        pairwise(),
        // Bootstrap on each arrival at 'valid' from a non-valid settled state: first launch, and
        // re-login after logout. Masquerade uses a page refresh, so valid→valid needs no handling.
        filter(([previousState, currentState]) => currentState?.state === 'valid' && previousState?.state !== 'valid')
      )
      .subscribe(([previousState, currentState]) => {
        console.log(
          '[APP-INITIALIZER] Token state transition:',
          previousState?.state || 'undefined',
          '->',
          currentState?.state,
          '- fetching provision before bootstrap'
        );

        this.fetchProvisionThenBootstrap();
      });
  }

  /**
   * Gate the app bootstrap on provisioning. Fetches provision immediately after the token becomes
   * valid; only on success do we flip `isProvisioned$` true and bring up sockets + feature data
   * (handles re-login after logout via the transition filter above). A not-provisioned account
   * (app_code 1002) or a persistent transient failure routes to the dedicated /not-provisioned page
   * instead of booting a broken, half-initialized session.
   *
   * defer() is required so retry re-invokes getProvision() for a fresh request — the underlying
   * ApiService call fires its promise eagerly, so a plain retry would replay the settled result.
   */
  private fetchProvisionThenBootstrap(): void {
    defer(() => this.appConfigService.getProvision())
      .pipe(
        retry({
          count: PROVISION_RETRY_COUNT,
          delay: (error, retryCount) => {
            // A not-provisioned response is deterministic — don't retry it.
            if (error?.error?.app_code === NOT_PROVISIONED_APP_CODE) {
              return throwError(() => error);
            }
            console.warn(`[APP-INITIALIZER] Provision fetch failed (attempt ${retryCount}), retrying`, error);
            return timer(PROVISION_RETRY_BACKOFF_MS * retryCount);
          },
        })
      )
      .subscribe({
        next: () => {
          console.log('[APP-INITIALIZER] Provision succeeded — bringing up sockets and services');
          this.authService.setProvisioned(true);
          this.initWebSocketService();
          this.initServices();
        },
        error: (error) => {
          this.authService.setProvisioned(false);
          this.appConfigService.notifyProvisionLoadFailed();
          console.error('[APP-INITIALIZER] Provision failed — routing to /not-provisioned', error);
          void this.router.navigate(['/not-provisioned'], {
            queryParams: { reason: error?.error?.message, code: error?.error?.app_code },
          });
        },
      });
  }

  private initServices() {
    console.log('[APP-INITIALIZER] initServices() called');

    // Unsubscribe from any existing data fetch subscription to prevent duplicate subscriptions
    // This is critical for the logout/login cycle - without this, each login creates a new
    // subscription while the old one remains active, causing duplicate data fetches
    if (this.dataFetchSubscription) {
      console.log('[APP-INITIALIZER] Unsubscribing from previous data fetch subscription');
      this.dataFetchSubscription.unsubscribe();
      this.dataFetchSubscription = null;
      this.stabilizedSubject.next(false);
    }

    this.services.forEach((service) => {
      const serviceName = service.constructor.name;
      const initStart = performance.now();
      service.init();
      const initDuration = Math.round(performance.now() - initStart);
      console.log(`[APP-INITIALIZER] init() ${serviceName} ${initDuration}ms`);
    });

    this.dataFetchSubscription = combineLatest([this.authService.isProvisioned$, this.webSocketService.isConnected$])
      .pipe(
        tap(([isProvisioned, isConnected]) => {
          console.log(
            `[APP-INITIALIZER] combineLatest check - isProvisioned: ${isProvisioned}, isConnected: ${isConnected}`
          );
        }),
        map(([isProvisioned, isConnected]) => isProvisioned && isConnected),
        tap((shouldFetch) => {
          console.log(`[APP-INITIALIZER] shouldFetch evaluated to: ${shouldFetch}`);
        }),
        startWith(false),
        distinctUntilChanged(),
        filter((shouldFetch) => shouldFetch),
        tap(() => {
          console.log('[APP-INITIALIZER] Starting data fetch sequence');
          this.isLoading$.next(true);
        }),
        concatMap(() => {
          const brandingStart = performance.now();
          console.log('[APP-INITIALIZER] Starting branding refresh');
          return this.brandingService.refreshData().pipe(
            tap(() => {
              const brandingDuration = Math.round(performance.now() - brandingStart);
              console.log(`[APP-INITIALIZER] Branding refresh complete ${brandingDuration}ms`);
            })
          );
        }),
        concatMap(() => {
          const refreshAllStart = performance.now();
          console.log('[APP-INITIALIZER] Starting refreshData() for all services');
          const dataFetches = this.services.map((service: { refreshData(): Observable<unknown> }, index: number) => {
            const serviceName = service.constructor.name;
            const refreshStart = performance.now();
            console.log(`[APP-INITIALIZER] Starting refreshData() for ${serviceName}`);
            return service.refreshData().pipe(
              tap(() => {
                const refreshDuration = Math.round(performance.now() - refreshStart);
                console.log(`[APP-INITIALIZER] refreshData() ${serviceName} ${refreshDuration}ms`);
                this.progress$.next((index * 100) / Math.max(this.services.length, 1));
              }),
              catchError((error) => {
                console.error(`[APP-INITIALIZER] ERROR in refreshData() for ${serviceName}:`, error);
                throw error;
              })
            );
          });
          return forkJoin(dataFetches).pipe(
            tap(() => {
              const totalRefreshDuration = Math.round(performance.now() - refreshAllStart);
              console.log(`[APP-INITIALIZER] All refreshData() complete ${totalRefreshDuration}ms`);
            })
          );
        }),
        finalize(() => {
          console.log('[APP-INITIALIZER] Data fetch sequence finalized');
          this.isLoading$.next(false);
        })
      )
      .subscribe({
        next: () => {
          const totalBootstrapDuration = Math.round(performance.now() - this.bootstrapStartTime);
          console.log(`[APP-INITIALIZER] ✓ App ready — total bootstrap ${totalBootstrapDuration}ms`);
          this.stabilizedSubject.next(true);
          this.isLoading$.next(false);
          this.appConfigService.getUserRoles();
          this.featureAnnouncementService.init();
          firstValueFrom(this.brandingService.brandingData$).then((brandingData) => {
            if (brandingData) {
              this.faviconService.changeFavicon(brandingData[BrandingField.FieldFavicon]);
            }
          });
        },
        error: (error) => {
          console.error('[APP-INITIALIZER] Error during service loading:', error);
        },
      });
  }

  private initWebSocketService() {
    const wsInitStart = performance.now();
    console.log('[APP-INITIALIZER] initWebSocketService() called');
    this.websocketServices.forEach((service) => {
      const serviceName = service.constructor.name;
      const bindStart = performance.now();
      service.bindSocketEvents();
      const bindDuration = Math.round(performance.now() - bindStart);
      console.log(`[APP-INITIALIZER] bindSocketEvents() ${serviceName} ${bindDuration}ms`);
    });
    this.webSocketService.init();
    const wsInitDuration = Math.round(performance.now() - wsInitStart);
    console.log(`[APP-INITIALIZER] initWebSocketService() complete ${wsInitDuration}ms`);
  }

  private setupAuthEventHandling() {
    // Set up logout event listener - same logic as AppComponent
    this.eventBusService
      .on(EventName.Logout)
      .pipe(
        exhaustMap((data) =>
          from(
            (async () => {
              console.log('[APP-INITIALIZER] Logout event received:', data);
              console.log('[APP-INITIALIZER] Current URL:', window.location.href);

              // Remove all socket event listeners before disconnecting
              console.log('[APP-INITIALIZER] Removing all socket event listeners before logout');
              this.webSocketService.removeAllListeners();

              this.webSocketService.disconnect();
              this.dialog.closeAll();
              this.phoneService.dispose();

              // Wait for all storage to be cleared before redirecting
              await this.authService.clearAccess();

              await this.authService.navigateForLogout(window, (data && data.reason) || undefined);
            })()
          ).pipe(
            catchError((err) => {
              console.error('[APP-INITIALIZER] Logout error:', err);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  }
}
